import { FC, useCallback, useEffect, useState } from 'react';

import { isCategory, NavItem } from './helpers';
import { NavigationItem } from './NavigationItem';
import { mergeRefs } from '@/helpers/common';
import { ThemeMode } from '@/helpers/enums';
import { useHorizontalScroll } from '@/hooks/common';
import { useDevice } from '@/hooks/device';
import { useElementSize } from '@/hooks/elementSize';
import useEventListener from '@/hooks/eventListener';
import { useNavigation } from '@/hooks/navigation';
import { useDetectShadow } from '@/hooks/shadow';

import { ScCategoriesWrapper, ScTabNavigation } from './styled';
import { paddingMob } from '@/styles/helpers';

interface TabNavigationProps {
  navItems: NavItem[];
  selectedItem: number;
  setSelectedItem?: (index: number) => void;
  menuItemWidth?: string;
  maxWidth100?: boolean;
  fullScreen?: boolean;
  containerWidth?: number;
  withoutShadow?: boolean;
  withBorderMarker?: boolean;
  withUnderlineMarker?: boolean;
  mode?: ThemeMode;
  handleNavigationItemClick?: (id: string) => void;
}

export const TabNavigation: FC<TabNavigationProps> = ({
  navItems,
  selectedItem,
  setSelectedItem,
  menuItemWidth,
  mode = ThemeMode.Dark,
  handleNavigationItemClick,
  ...props
}) => {
  const { isMobileDevice } = useDevice();

  const { generateLink } = useNavigation();
  const refWithScroll = useHorizontalScroll();
  const [ref, { width }] = useElementSize();
  const { ref: shadowRef, isLeft, isRight } = useDetectShadow();

  const [containerClientWidth, setContainerClientWidth] = useState<number>(0);

  const [selectedItemPosition, setSelectedItemPosition] = useState({
    left: 0,
    width: 0,
  });

  const [padding, setPadding] = useState(0);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      ref.current?.scroll({
        left: selectedItemPosition.left,
        behavior: 'smooth',
      });
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [ref, selectedItemPosition.left]);

  const handleSelectItem = (index: number, id: string | null) => {
    if (id) handleNavigationItemClick && handleNavigationItemClick(id);
    setSelectedItem && setSelectedItem(index);
  };

  const paddingLeftRight = useCallback(() => {
    const windowWidth = window.innerWidth;

    if (isMobileDevice) {
      setPadding((windowWidth - containerClientWidth + paddingMob * 2) / 2);
    }
  }, [isMobileDevice, containerClientWidth]);

  useEventListener('resize', paddingLeftRight, undefined, true);

  return (
    <ScCategoriesWrapper
      ref={(node) => {
        if (node) {
          setContainerClientWidth(node.clientWidth);
        }
      }}
      padding={padding}
      paddingMob={paddingMob}
      {...props}
    >
      <div className="mob-full">
        <ScTabNavigation
          ref={mergeRefs(ref, refWithScroll, shadowRef)}
          left={selectedItemPosition.left}
          width={selectedItemPosition.width}
          padding={padding}
          isShadowLeft={isLeft}
          isShadowRight={isRight}
          mode={mode}
          {...props}
        >
          <div>
            {selectedItem >= 0 && <div className="marker" />}
            {navItems.map((navItem, index) => (
              <NavigationItem
                key={index}
                width={menuItemWidth}
                title={isCategory(navItem) ? navItem.title : navItem}
                isSelected={index === selectedItem}
                setSelectedItemPosition={setSelectedItemPosition}
                handleSelectItem={() =>
                  handleSelectItem(
                    index,
                    isCategory(navItem) ? navItem.id : null
                  )
                }
                widthOfMenu={width}
                mode={mode}
                navItemsCount={navItems.length}
                link={
                  isCategory(navItem)
                    ? generateLink(navItem.pathname)
                    : undefined
                }
                {...props}
              />
            ))}
          </div>
        </ScTabNavigation>
      </div>
    </ScCategoriesWrapper>
  );
};
